import { canvasRequest } from '$/api/canvasRquest';
import { roboneoApi } from '$/api/request';
import APP from '$/api/api';
import { v4 as uuid } from 'uuid';
import {
  type CreateNodeExecuteTaskOptions,
  type CreateNodeExecuteTaskResult,
  type NodeExecuteHistoryResult,
  type NodeExecuteTaskParams,
  type NodeExecuteTaskResult,
  type NodeListItem,
  SlideTabEnum,
} from './type';
import { EdgeType } from '../edge/type';

/** nodeexecute 创建任务可能包含同步预处理，单次响应最多等待一分钟。 */
export const NODE_EXECUTE_REQUEST_TIMEOUT_MS = 60_000;

/** 归一化前置工具协议；未声明前置阶段的任务保持字段缺省。 */
function normalizePreToolProtocol(item: NodeExecuteTaskParams): NodeExecuteTaskParams {
  const normalizedItem: NodeExecuteTaskParams = {
    ...item,
  };
  if (!Array.isArray(item.pre_tools)) {
    delete normalizedItem.pre_tools;
    delete normalizedItem.pre_type;
    return normalizedItem;
  }
  if (item.pre_tools.length === 0) {
    delete normalizedItem.pre_type;
  }
  return normalizedItem;
}

/** 判断 params 是否已按 node_list_array 的内层工具链分组。 */
function isGroupedNodeExecuteTaskParams(
  params: CreateNodeExecuteTaskOptions['params'],
): params is NodeExecuteTaskParams[][] {
  return Array.isArray(params) && Array.isArray(params[0]);
}

/** 将兼容的任务入参转换为后端 node_list_array 二维结构。 */
function buildNodeListArray(
  params: CreateNodeExecuteTaskOptions['params'],
): NodeExecuteTaskParams[][] {
  if (isGroupedNodeExecuteTaskParams(params)) {
    return params.map((group) => group.map(normalizePreToolProtocol));
  }
  if (Array.isArray(params)) {
    return params.map((item) => [normalizePreToolProtocol(item)]);
  }
  return [[normalizePreToolProtocol(params)]];
}

/** 从单任务、独立任务列表或工具链中读取首个节点 id。 */
function getFirstNodeId(params: CreateNodeExecuteTaskOptions['params']): string | undefined {
  if (isGroupedNodeExecuteTaskParams(params)) {
    return params[0]?.[0]?.node_id;
  }
  return Array.isArray(params) ? params[0]?.node_id : params.node_id;
}

export async function nodeList(params: {
  name?: string;
  // 游标
  cursor?: number;
  // 每页条数
  count?: number;
}) {
  return canvasRequest.request(
    {
      method: 'GET',
      url: APP.roboneo.api.nodeList,
      params,
    },
  );
}

export async function getNodeList(params: {
  name?: string;
  // 游标
  cursor?: number | null;
  // 每页条数
  count?: number ;
  type?: SlideTabEnum;
}) {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise<{ cursor: number;list: NodeListItem[]; count: number; }>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      params: {
        ...params,
      },
      method: 'GET',
      url: APP.roboneo.api.nodeList,
    });

    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data);
      return;
    }
    reject(response);
  });
}

export async function getNode(params: {
  node_id: string;
}) {
  return canvasRequest.request(
    {
      method: 'GET',
      url: APP.roboneo.api.nodeInfo,
      params,
    },
  );
}
export async function nodeUpdate(data: {
  name?: string;
  cover?: string;
  data?: string;
  node_id?: number;
  // 状态 1 正常，2删除
  client_status: 1 | 2;
}) {
  return canvasRequest.request(
    {
      method: 'POST',
      url: APP.roboneo.api.nodeSave,
      data,
    },
  );
}

/**
 * 创建节点执行任务
 *
 * @export
 * @param {CreateNodeExecuteTaskOptions} options
 * @return {*}
 */
export async function createNodeExecuteTask({
  room_id,
  group_id,
  params,
  passThroughTaskId,
  workflow_version,
  extra,
  business_type,
  taskSpeed,
  media_compress_url_map,
}: CreateNodeExecuteTaskOptions) {
  const node_id = getFirstNodeId(params);
  const node_list_array = buildNodeListArray(params);
  const parameter: any = {
    path_scene: 'nodeexecute',
    need_node_name: true,
    room_id,
    node_id,
    group_id: group_id ?? uuid(),
    extra: extra
      ? {
        ...extra,
        big_data_patch: {
          position_type: '/team_studio',
        },
      }
      : undefined,
    node_list_array,
    media_compress_url_map,
    workflow_version,
    ...(business_type ? { business_type } : {}),
    ...(taskSpeed ? { task_speed: taskSpeed } : {}),
  };
  if (passThroughTaskId) {
    parameter.pass_through_task_id = passThroughTaskId;
  }
  const response = await roboneoApi.request(
    {
      data: {
        parameter,
      },
      method: 'POST',
      timeout: NODE_EXECUTE_REQUEST_TIMEOUT_MS,
      url: APP.roboneo.api.path,
    },
  );
  return new Promise<CreateNodeExecuteTaskResult | CreateNodeExecuteTaskResult[]>((resolve, reject) => {
    if (response.data.error_code === 0) {
      const tasks = workflow_version === 'v2' ? response.data.parameter.tasks : response.data.parameter.tasks[0];
      resolve(tasks);
    } else {
      reject(response.data);
    }
  });
}

/**
 * 查询节点执行任务
 *
 * @export
 * @param {string} task_id
 * @param {number} result_state 结果状态筛选
 * @return {*}
 */
export async function queryNodeExecuteTask(
  task_ids?: string[],
  room_id?: string,
  invalid_node_types?: string[],
  room_ids?: string[],
  page?: number,
  page_size?: number,
  business_type?: string,
  result_state?: number,
) {
  const parameter: Record<string, any> = {
    path_scene: 'nodeexecutequery',
    ...(room_id ? { room_id } : {}),
    invalid_node_types,
    room_ids,
    page,
    page_size,
    ...(business_type ? { business_type } : {}),
    ...(result_state !== undefined ? { result_state } : {}),
  };
  if (task_ids !== undefined) {
    parameter.task_ids = task_ids;
  } else if (!business_type) {
    parameter.task_ids = [];
  }

  const response = await roboneoApi.request(
    {
      data: {
        parameter,
      },
      method: 'POST',
      url: APP.roboneo.api.path,
    },
  );
  return new Promise<NodeExecuteTaskResult>((resolve, reject) => {
    if (response.data.error_code === 0) {
      resolve(response.data.parameter);
    } else {
      reject(response.data);
    }
  });
}

/**
 * 按业务类型删除节点执行任务。
 *
 * @param task_ids 待删除任务 id 列表
 * @param business_type 任务所属业务类型
 * @returns 删除成功后结束
 */
export async function deleteNodeExecuteTask(
  task_ids: string[],
  business_type: string,
): Promise<void> {
  const response = await roboneoApi.request({
    data: {
      parameter: {
        path_scene: 'nodeexecutedelete',
        business_type,
        task_ids,
      },
    },
    method: 'POST',
    url: APP.roboneo.api.path,
  });
  if (response.data.error_code !== 0) {
    throw response.data;
  }
}

/** 按当前登录用户和 MCP 名称查询 nodeexecute 历史，身份参数由 roboneoApi 统一注入。 */
export async function queryNodeExecuteHistory(mcpName: string): Promise<NodeExecuteHistoryResult> {
  const response = await roboneoApi.request({
    data: {
      parameter: {
        path_scene: 'nodeexecutequery',
        mcp_name: mcpName,
      },
    },
    method: 'POST',
    url: APP.roboneo.api.path,
  });
  if (response.data.error_code === 0) {
    return response.data.parameter as NodeExecuteHistoryResult;
  }
  throw response.data;
}

// 提交节点执行任务
export async function nodeExecute(params: {
  retry_task_id?: string;
  room_id: string;
  tool_scene: string;
  estimate_only?: boolean; // 是否只估算时间
  graph: {
    nodes: Array<any>; // 节点数组
    edges: EdgeType[]; // 边数组
  }; // 图结构数据
  extra?: any;
}): Promise<{
    error_code: number;
    error_msg: string;
    msg_id: string;
    parameter: {
      tasks: Array<{
        task_id: string;
        estimated_total_ms: number;
      }>;
    };
    media_info_list: [];
    duration: {
      created_timestamp: number;
      pull_timestamp: number;
      waiting_time: number;
      alg_process_time: number;
      upload_time: number;
      repost_time: number;
    };
  }> {
  const response = await roboneoApi.request(
    {
      method: 'POST',
      data: {
        parameter: {
          ...params,
          path_scene: 'nodeexecute',
          node_type: 'graph',
          need_node_name: true,
        },
      },
      timeout: NODE_EXECUTE_REQUEST_TIMEOUT_MS,
      url: APP.roboneo.api.path,
    },
  );
  return new Promise((resolve, reject) => {
    if (response.data.error_code === 0) {
      resolve(response.data);
    } else {
      reject(new Error(response.data.error_msg));
    }
  });
}
